import React from 'react';
import DOMPurify from 'dompurify';
import moment from 'moment-jalaali';
import 'moment/locale/fa';
import Dialog from 'components/dialog/dialog';
import { alpha, Box, Button, CircularProgress, Typography } from '@mui/material';
import { NewsItemType } from 'features/news/news.types';


type ShowNewsProps = {
    news?: NewsItemType | null;
    isOpen?: boolean;
    onClose?: () => void;
    onRead?: () => void;
    isReading?: boolean;
    showReadAction?: boolean;
};

function prepareNewsHTML(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    doc.querySelectorAll('a').forEach((a) => {
        const rawHref = (a.getAttribute('href') || '').trim();
        if (!rawHref) return;

        const isSpecialHref = /^(https?:\/\/|mailto:|tel:|#|\/)/i.test(rawHref);
        if (!isSpecialHref) {
            a.setAttribute('href', `https://${rawHref}`);
        }

        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
    });

    return doc.body.innerHTML;
}

const ShowNews: React.FC<ShowNewsProps> = ({
    news = null,
    isOpen = false,
    onClose = () => undefined,
    onRead,
    isReading = false,
    showReadAction = false,
}): JSX.Element => {
    const sanitizedHTML = React.useMemo(
        () =>
            news
                ? DOMPurify.sanitize(prepareNewsHTML(news.content || ''), {
                      USE_PROFILES: { html: true },
                      ADD_ATTR: ['target', 'rel', 'class', 'style'],
                  })
                : '',
        [news],
    );

    const newsDate = React.useMemo(() => {
        if (!news) return '';
        moment.loadPersian({ dialect: 'persian-modern', usePersianDigits: true });
        return moment(news.updated_at || news.created_at)
            .locale('fa')
            .format('jD jMMMM jYYYY');
    }, [news]);

    return (
        <Dialog
            label={
                <Box
                    sx={{
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        columnGap: 1,
                        rowGap: 0.35,
                    }}
                >
                    <Typography
                        component="span"
                        className="font-yekan-heavy"
                        sx={{ fontSize: 16, lineHeight: 1.7, color: 'text.primary' }}
                    >
                        {news?.title}
                    </Typography>
                    {newsDate ? (
                        <Typography
                            component="span"
                            sx={{
                                fontSize: 11.5,
                                lineHeight: 1.7,
                                fontWeight: 400,
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {newsDate}
                        </Typography>
                    ) : null}
                </Box>
            }
            open={isOpen}
            onClose={onClose}
            icon={
                <Box
                    component="i"
                    className="fa-sharp-duotone fa-solid fa-bullhorn"
                    sx={(theme) => ({
                        color:
                            theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                    })}
                />
            }
            maxWidth="sm"
            width="680px"
        >
            <Box
                sx={(theme) => ({
                    width: '100%',
                    minWidth: 0,
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor:
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.08)
                            : alpha(theme.palette.common.black, 0.075),
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.018)
                            : alpha(theme.palette.primary.main, 0.018),
                })}
            >
                <Box
                    className="news-rich-content"
                    sx={(theme) => ({
                        width: '100%',
                        minWidth: 0,
                        color: 'text.primary',
                        direction: 'rtl',
                        textAlign: 'right',
                        fontSize: 13.5,
                        lineHeight: 2.05,
                        fontWeight: 400,
                        '&, & *': {
                            fontFamily: 'inherit',
                        },
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',

                        '& > :first-of-type': { mt: 0 },
                        '& > :last-child': { mb: 0 },
                        '& p': {
                            my: 1,
                            lineHeight: 2.05,
                            fontWeight: 400,
                            whiteSpace: 'normal',
                        },
                        '& strong, & b': {
                            fontWeight: 700,
                        },
                        '& em, & i:not([class*="fa-"])': {
                            fontStyle: 'italic',
                        },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                            mt: 2,
                            mb: 1,
                            lineHeight: 1.75,
                            color: 'text.primary',
                        },
                        '& h1': { fontSize: 23 },
                        '& h2': { fontSize: 20 },
                        '& h3': { fontSize: 17.5 },
                        '& h4, & h5, & h6': { fontSize: 15.5 },
                        '& ul, & ol': {
                            my: 1,
                            pr: 2.5,
                            pl: 0,
                        },
                        '& li': {
                            mb: 0.45,
                        },
                        '& a': {
                            color:
                                theme.palette.mode === 'dark'
                                    ? theme.palette.primary.light
                                    : theme.palette.primary.main,
                            textDecoration: 'none',
                            borderBottom: `1px dashed ${alpha(
                                theme.palette.mode === 'dark'
                                    ? theme.palette.primary.light
                                    : theme.palette.primary.main,
                                0.45,
                            )}`,
                        },
                        '& blockquote': {
                            my: 1.5,
                            mx: 0,
                            py: 1,
                            px: 1.5,
                            borderInlineStart: '3px solid',
                            borderColor:
                                theme.palette.mode === 'dark'
                                    ? 'primary.light'
                                    : 'primary.main',
                            borderRadius: 1,
                            backgroundColor: alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.08 : 0.045,
                            ),
                            color: 'text.secondary',
                        },
                        '& figure': {
                            m: 0,
                            my: 1.5,
                            maxWidth: '100%',
                        },
                        '& figcaption': {
                            mt: 0.6,
                            color: 'text.secondary',
                            fontSize: 11.5,
                            textAlign: 'center',
                        },
                        '& img': {
                            display: 'block',
                            maxWidth: '100%',
                            height: 'auto',
                            my: 1.5,
                            mx: 'auto',
                            borderRadius: 1.5,
                        },
                        '& hr': {
                            my: 2,
                            border: 0,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        },
                        '& table': {
                            width: '100%',
                            minWidth: 480,
                            borderCollapse: 'collapse',
                            my: 1.5,
                        },
                        '& th, & td': {
                            px: 1.2,
                            py: 0.9,
                            border: '1px solid',
                            borderColor: 'divider',
                            textAlign: 'right',
                            verticalAlign: 'top',
                        },
                        '& th': {
                            backgroundColor: alpha(
                                theme.palette.text.primary,
                                theme.palette.mode === 'dark' ? 0.06 : 0.035,
                            ),
                            fontWeight: 700,
                        },
                        '& pre': {
                            direction: 'ltr',
                            textAlign: 'left',
                            overflowX: 'auto',
                            p: 1.5,
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                                theme.palette.common.black,
                                theme.palette.mode === 'dark' ? 0.2 : 0.055,
                            ),
                        },
                        '& code': {
                            direction: 'ltr',
                            fontFamily: 'monospace',
                        },
                    })}
                >
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: sanitizedHTML,
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {showReadAction && news?.read_news !== true && (
                <Button
                    fullWidth
                    variant="contained"
                    disabled={isReading}
                    onClick={onRead}
                    sx={{ mt: 1.75, minHeight: 46, borderRadius: 1.5 }}
                >
                    {isReading ? (
                        <CircularProgress size={18} color="inherit" />
                    ) : (
                        'خواندم'
                    )}
                </Button>
            )}
        </Dialog>
    );
};

export default ShowNews;
